/**
 * The team's parent link on the done screens: the registration's /j/<token>
 * page on TourneyNation. A parent opens it without an account, adds their
 * player (name, birth date, jersey and the organizer's roster fields) and
 * their contact details, and signs the TourneyNation Player Waiver and the
 * tournament's waivers. The player lands on the team and event rosters and
 * the signatures count toward the organizer's compliance view.
 *
 * One card for a single registration, one row per event for bulk. The card
 * renders straight away with its copy and a held space for each link, so
 * the screen doesn't shift when the links arrive; it goes only if none of
 * them can be loaded.
 */
import { useEffect, useState } from 'react';

import type { EmbedClient, ParentLink } from '@/api/embed';
import './ParentLink.css';

export interface ParentLinkFor {
  teamId: string;
  registrationId: string;
  /** Named beside the link when there are several (bulk), and in the text. */
  eventName: string;
}

function closedLine(link: ParentLink): string {
  if (link.closed_reason === 'locked') return 'Rosters are locked, so this link no longer accepts players.';
  if (link.closed_reason === 'not_registered') return 'This link opens once your team is confirmed in the tournament.';
  return 'The organizer has turned this link off.';
}

/** Copy to the clipboard: the Clipboard API first (needs the iframe's
 *  clipboard-write permission, which loader.js grants), then the older
 *  execCommand route. False when neither worked. */
async function copyText(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      // A browser waiting on a permission it will never get mustn't leave
      // the button stuck: give it a second, then use the older route.
      const wrote = await Promise.race([
        navigator.clipboard.writeText(text).then(() => true),
        new Promise<boolean>((resolve) => window.setTimeout(() => resolve(false), 1000)),
      ]);
      if (wrote) return true;
    }
  } catch { /* blocked: try the older route */ }
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.setAttribute('readonly', '');
    ta.style.cssText = 'position:fixed;top:0;left:0;opacity:0;pointer-events:none';
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand('copy');
    ta.remove();
    return ok;
  } catch {
    return false;
  }
}

/** Select an element's text, for copying by hand when the browser refuses. */
function selectText(el: HTMLElement | null) {
  if (!el) return;
  const range = document.createRange();
  range.selectNodeContents(el);
  const sel = window.getSelection();
  sel?.removeAllRanges();
  sel?.addRange(range);
}

const MAC = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent);

function LinkActions({ link, eventName, urlId }: { link: ParentLink | null; eventName: string; urlId: string }) {
  const [copied, setCopied] = useState<null | 'done' | 'manual'>(null);
  const sms = link
    ? `sms:?&body=${encodeURIComponent(
      `Please add your player to our roster for ${eventName} and sign the required waivers. It takes a few minutes and no account is needed: ${link.url}`,
    )}`
    : undefined;
  return (
    <div className="tn-plink__acts">
      <a
        className="tn-btn tn-btn--secondary tn-plink__btn"
        href={sms}
        aria-disabled={!link}
        onClick={(e) => { if (!link) e.preventDefault(); }}
      >
        Text link
      </a>
      <button
        type="button"
        className="tn-btn tn-btn--secondary tn-plink__btn"
        disabled={!link}
        onClick={async () => {
          if (!link) return;
          const ok = await copyText(link.url);
          // Couldn't write it: select the link so it's one keystroke away.
          if (!ok) selectText(document.getElementById(urlId));
          setCopied(ok ? 'done' : 'manual');
          window.setTimeout(() => setCopied(null), ok ? 1800 : 4000);
        }}
      >
        {copied === 'done' ? 'Copied' : copied === 'manual' ? `Press ${MAC ? '⌘C' : 'Ctrl+C'} to copy` : 'Copy link'}
      </button>
    </div>
  );
}

export function ParentLinks({ client, items, inEmail = false }: {
  client: EmbedClient;
  items: ParentLinkFor[];
  /** The confirmation email carries the link too (sent for a confirmed
   *  registration): say so, so the coach knows where to find it later. */
  inEmail?: boolean;
}) {
  const [links, setLinks] = useState<Record<string, ParentLink | 'failed'>>({});
  const key = items.map((i) => i.registrationId).join(',');

  useEffect(() => {
    let off = false;
    for (const i of items) {
      client.getParentLink(i.teamId, i.registrationId)
        // A reply without a link counts as unavailable, never a broken screen.
        .then((l) => { if (!off) setLinks((prev) => ({ ...prev, [i.registrationId]: l?.url ? l : 'failed' })); })
        .catch(() => { if (!off) setLinks((prev) => ({ ...prev, [i.registrationId]: 'failed' })); });
    }
    return () => { off = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, key]);

  const shown = items.filter((i) => links[i.registrationId] !== 'failed');
  if (shown.length === 0) return null;
  const many = items.length > 1;
  const linkOf = (id: string) => {
    const l = links[id];
    return l && l !== 'failed' ? l : null;
  };
  const single = !many ? linkOf(shown[0].registrationId) : null;
  const anyOpen = shown.some((i) => linkOf(i.registrationId)?.open ?? true);

  return (
    <section className="tn-plink" aria-label="Invite parents">
      <h2 className="tn-plink__title">Invite parents to complete the roster</h2>
      <p className="tn-plink__sub">
        {single && !single.open
          ? closedLine(single)
          : `Share this link with your team’s parents. Each parent enters their player’s details and signs the required waivers, with no account needed. Players are added to your roster as each parent finishes.${
            inEmail ? ` You’ll also find ${many ? 'these links' : 'this link'} in your confirmation email.` : ''}`}
      </p>
      {shown.map((i) => {
        const link = linkOf(i.registrationId);
        return (
          <div key={i.registrationId} className="tn-plink__item">
            {many ? <span className="tn-plink__event">{i.eventName}</span> : null}
            <span id={`tn-plink-${i.registrationId}`} className={`tn-plink__url${link ? '' : ' tn-plink__url--loading'}`}>
              {link ? link.url.replace(/^https?:\/\//, '') : 'Getting your link…'}
            </span>
            {!link || link.open
              ? <LinkActions link={link} eventName={i.eventName} urlId={`tn-plink-${i.registrationId}`} />
              : many ? <span className="tn-plink__closed">{closedLine(link)}</span> : null}
          </div>
        );
      })}
      {anyOpen ? (
        <p className="tn-plink__fine">The link stays open until the organizer locks rosters.</p>
      ) : null}
    </section>
  );
}
